import React,{ useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from '../../components/Picker/DatePicker';
import TimePicker from '../../components/Picker/TimePicker';
import EquipmentRatingPopup from '../../components/Popup/EquipmentRatingPopup';
import dayjs from 'dayjs';
import product from "../../assets/img/detailproduct.png";
import { TERMS_LIST_URL, MY_INFO_URL, STORE_LIST_URL } from "../../utils/urls";
import instance from "../../services/services";
import { formatPhoneNumber } from "../../utils/validation";
import orderApi from "../../services/Order/orderApi";
import PaymentService from "../../services/Payment/PaymentService";
import EstimatePopup from "../../components/Popup/EstimatePopup";
import PopupLoginError from "../../components/Popup/LoginError";
import { formatLocalString } from "../../utils/validation";
import { useLanguage } from "../../hooks/useLanguage";
import TermsPopup from "../../components/Popup/TermsPopup";

const normalizeCartList = (cartList = {}) => ({
  bundleMain: (cartList.bundleMain || []).map(item => ({
    ...item,
    isChecked: item?.isChecked !== undefined ? item.isChecked : true,
  })),
  bundleAdd: (cartList.bundleAdd || []).map(item => ({
    ...item,
    isChecked: item?.isChecked !== undefined ? item.isChecked : true,
  })),
  bundleAcc: (cartList.bundleAcc || []).map(item => ({
    ...item,
    isChecked: item?.isChecked !== undefined ? item.isChecked : true,
  })),
});

function QuotationForm(){
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [memo, setMemo] = useState('');
  const rentalSelectRef = useRef(null);
  const returnSelectRef = useRef(null);
  const [storeList, setStoreList] = useState([]);
  const[isPopup, setIsPopup] = useState("")
  const[isPopupLoginError, setIsPopupLoginError] = useState("")
  const bankSelectRef = useRef(null); // 은행 선택
  const [selectBank, setSelectBank] = useState({
    isOpen:false,
    bankItem: '',
    bankValue: ''
  });
 
  const [isShow, setIsShow] = useState({}); // 패키지 보기 버튼
  const [myInfo, setMyInfo] = useState(null); // 내정보 데이터

  // 상품 데이터 관리를 위한 상태 추가
  const [cartListData, setCartListData] = useState({
    bundleMain: [],
    bundleAdd: [],
    bundleAcc: []
  });
  
  // 주문 요약 정보 상태 추가
  const [orderSummary, setOrderSummary] = useState(null);
  
  const isItemChecked = useCallback((item) => item?.isChecked !== undefined ? item.isChecked : true, []);

  const selectedSummary = useMemo(() => {
    const toNumber = (value) => {
      if (value === null || value === undefined || value === '') return 0;
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
      }
      if (typeof value === 'string') {
        const cleaned = value.replace(/,/g, '').trim();
        if (!cleaned) return 0;
        const num = Number(cleaned);
        return Number.isFinite(num) ? num : 0;
      }
      return 0;
    };

    const calcTotals = (items = []) => items.reduce((acc, item) => {
      if (!isItemChecked(item)) return acc;
      
      const eaRaw = toNumber(item.ea ?? 0);
      const ea = Number.isFinite(eaRaw) && eaRaw > 0 ? eaRaw : 1;
      
      // sum_sale_price가 있으면 이미 총 가격(수량 * 단가)으로 계산된 값
      // 없으면 단가를 찾아서 수량을 곱해야 함
      let itemTotalPrice = 0;
      
      if (item.sum_sale_price !== undefined && item.sum_sale_price !== null && item.sum_sale_price !== '') {
        // sum_sale_price는 이미 총 가격
        itemTotalPrice = toNumber(item.sum_sale_price);
      } else if (item.total_price !== undefined && item.total_price !== null && item.total_price !== '') {
        // total_price도 이미 총 가격일 가능성이 높음
        itemTotalPrice = toNumber(item.total_price);
      } else {
        // 단가를 찾아서 수량을 곱함
        const unitPrice = 
          toNumber(item.price) ||
          toNumber(item.unit_rental_12_price) ||
          toNumber(item.unit_rental_24_price) ||
          0;
        itemTotalPrice = unitPrice * ea;
      }

      return {
        ea: acc.ea + ea,
        price: acc.price + itemTotalPrice,
      };
    }, { ea: 0, price: 0 });

    const main = calcTotals(cartListData.bundleMain || []);
    const add = calcTotals(cartListData.bundleAdd || []);
    const acc = calcTotals(cartListData.bundleAcc || []);

    const totalRentalEA = main.ea + add.ea;
    const totalRentalPrice = main.price + add.price;
    const totalEA = totalRentalEA + acc.ea;
    const totalPrice = totalRentalPrice + acc.price;

    // 서버에서 계산된 settlePrice가 있으면 우선 사용 (더 정확함)
    const serverSettlePrice = orderSummary?.totalInfo?.settlePrice;
    const finalPrice = serverSettlePrice !== undefined && serverSettlePrice !== null && serverSettlePrice !== ''
      ? toNumber(serverSettlePrice)
      : totalPrice;

    return {
      totalMainEA: main.ea,
      totalMainPrice: main.price,
      totalAddEA: add.ea,
      totalAddPrice: add.price,
      totalAccEA: acc.ea,
      totalAccPrice: acc.price,
      totalRentalEA,
      totalRentalPrice,
      totalEA,
      totalPrice: finalPrice,
    };
  }, [cartListData, isItemChecked, orderSummary]);
  
  // 결제 방식 상태 추가 (기본값: 일시불)
  const [paymentMethod, setPaymentMethod] = useState('lump_sum');

  // 분할 결제 선택 시 필요한 상태 추가
  const [paymentDetails, setPaymentDetails] = useState({
    request_deposit: false,
    request_second_deposit: false,
    request_last_pay: false
  });

  // 장비평점 팝업
  const [isEquipmentPopup, setEquipmentPopup ] = useState(false);
  const handleEquipmentPopup = () => {
    setEquipmentPopup(true)
  };

  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isChecked, setIsChecked] = useState({
    option1: false,
    option2: false,
    option3: false,
    option4: false,
  });

  // 이전 페이지에서 전달된 데이터 확인
  const quotationData = location.state;
  
  // 대여지점, 반납지점 상태 초기화
  const [rentalState, setRentalState] = useState({
    isOpen: false,
    selectItem: quotationData?.rentalStore?.selectItem || '',
    selectValue: quotationData?.rentalStore?.selectValue || '',
    storeInfo: quotationData?.rentalStore?.storeInfo || null
  });
  
  // 반납상태
  const [returnState, setReturnState] = useState({
    isOpen: false,
    selectItem: quotationData?.returnStore?.selectItem || '',
    selectValue: quotationData?.returnStore?.selectValue || '',
    storeInfo: quotationData?.returnStore?.storeInfo || null
  });
  
  // 대여 날짜/시간 상태 초기화 (문자열로 전달받은 날짜를 dayjs 객체로 변환)
  const [rentalDateTime, setRentalDateTime] = useState({
    date: quotationData?.rentalDateTime?.date ? dayjs(quotationData.rentalDateTime.date) : dayjs(),
    time: quotationData?.rentalDateTime?.time || ''
  });
  
  // 반납 날짜/시간 상태 초기화 (문자열로 전달받은 날짜를 dayjs 객체로 변환)
  const [returnDateTime, setReturnDateTime] = useState({
    date: quotationData?.returnDateTime?.date ? dayjs(quotationData.returnDateTime.date) : dayjs(),
    time: quotationData?.returnDateTime?.time || ''
  });
  
  // 반납미정/장기연장 관련 코드 모두 제거
  // const [isUndecided, setIsUndecided] = useState(false);
  // const handleUndecidedCheck = (e) => {
  //   setIsUndecided(e.target.checked);
  // };
  // useEffect(() => {
  //   if (isUndecided) {
  //     setReturnState({
  //       isOpen: false,
  //       selectItem: '',
  //       selectValue: ''
  //     });
  //     setReturnDateTime({
  //       date: dayjs(),
  //       time: ''
  //     });
  //   }
  // }, [isUndecided]);

  // 내 정보 조회
  useEffect(()=> {
    const fetchMyInfo = async () => {
      try{
        const response = await instance.post(MY_INFO_URL);
        setMyInfo(response.data.data);
      } catch (error) {
        console.error('내정보 조회 실패:', error);
      }
    };
    fetchMyInfo();
  }, []);

  // 초기 데이터 로딩을 위한 useEffect 수정
  useEffect(() => {
    // 이전 페이지에서 전달된 선택된 상품 데이터 확인
    const selectedProductIds = quotationData?.selectedProducts?.ids || [];
    
    // API 호출을 위한 파라미터 생성
    const params = {
      additional_sale: "",
      allBalanceUse: "N",
      allPointUse: "N",
      cart_change_goods: [],
      cart_select_goods: "",
      deposit_price: "",
      download_seq: "",
      order_type: "visit",
      pay_select_goods: selectedProductIds.length > 0 ? selectedProductIds : [],
      point: "",
      rental_edate: returnDateTime.date 
                  ? `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_sdate: rentalDateTime.date 
                  ? `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_store_seq: rentalState.selectValue || "",
      return_store_seq: returnState.selectValue || "",
      undecidedChk: "N"
    };

    // 선택된 상품이 있는 경우에만 API 호출
    if (selectedProductIds.length > 0) {
      orderApi.getCartDetail(params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            // 상품 데이터 업데이트
            setCartListData(normalizeCartList(data.cartList));
            // 주문 요약 정보 업데이트
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('견적서 데이터 로딩 실패:', error);
        });
    } else if (quotationData?.selectedProducts?.cartListData) {
      // API 호출이 아직 안 된 경우, 전달받은 데이터가 있으면 해당 데이터로 초기화
      setCartListData(normalizeCartList(quotationData.selectedProducts.cartListData));
    }
  }, [quotationData, rentalDateTime.date, rentalDateTime.time, rentalState.selectValue, returnDateTime.date, returnDateTime.time, returnState.selectValue]);

  // 재고 부족 항목이 있는 패키지 자동 펼치기 useEffect 추가
  useEffect(() => {
    if (cartListData && cartListData.bundleMain && cartListData.bundleMain.length > 0) {
      const newIsShow = {};
      
      cartListData.bundleMain.forEach(item => {
        let hasStockShortage = false;
        
        // bundlePkg 중에서 stock_flag가 'N'인 항목이 있는지 확인
        if (item?.bundlePkg && item.bundlePkg.length > 0) {
          hasStockShortage = item.bundlePkg.some(pkgItem => pkgItem?.stock_flag === 'N');
        }
        
        // bundleCp 중에서 stock_flag가 'N'인 항목이 있는지 확인
        if (!hasStockShortage && item?.bundleCp && item.bundleCp.length > 0) {
          hasStockShortage = item.bundleCp.some(cpItem => cpItem?.stock_flag === 'N');
        }
        
        // 재고 부족이 있으면 해당 패키지를 펼침
        if (hasStockShortage && item?.cart_option_seq) {
          newIsShow[item.cart_option_seq] = true;
        }
      });
      
      // 재고 부족으로 펼쳐야 할 항목이 있으면 상태 업데이트
      if (Object.keys(newIsShow).length > 0) {
        setIsShow(prev => ({
          ...prev,
          ...newIsShow
        }));
      }
    }
  }, [cartListData]);

  const handleAllCheck = () => {
    setIsAllChecked(!isAllChecked);
    const newCheckedState = {
      option1: !isAllChecked,
      option2: !isAllChecked,
      option3: !isAllChecked,
      option4: !isAllChecked,
    };
    setIsChecked(newCheckedState);
  };

  const handleCheck = (option) => {
    const newCheckedState = { ...isChecked, [option]: !isChecked[option] };
    setIsChecked(newCheckedState);
    setIsAllChecked(Object.values(newCheckedState).every(Boolean));
  };



  // 드롭다운 열고 닫기 기능 - useCallback으로 메모이제이션
  const handleRentalSelectOpen = useCallback(() => {
    setRentalState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const handleReturnSelectOpen = useCallback(() => {
    setReturnState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  // 드롭다운에서 항목을 선택하는 기능 - useCallback으로 메모이제이션
  const handleRentalSelectItem = useCallback((select) => {
    setRentalState({
      isOpen: false,
      selectItem: select.store_name,
      selectValue: select.store_seq,
      storeInfo: {
        startHour: select.opening_start_hour,
        startMin: select.opening_start_min,
        endHour: select.opening_end_hour,
        endMin: select.opening_end_min,
        isFullTime: select.opening_hour_set === 'N' || 
                    (select.opening_start_hour === '00' && select.opening_end_hour === '00')
      }
    });
  }, []);

  const handleReturnSelectItem = useCallback((select) => {
    setReturnState({
      isOpen: false,
      selectItem: select.store_name,
      selectValue: select.store_seq,
      storeInfo: {
        startHour: select.opening_start_hour,
        startMin: select.opening_start_min,
        endHour: select.opening_end_hour,
        endMin: select.opening_end_min,
        isFullTime: select.opening_hour_set === 'N' || 
                    (select.opening_start_hour === '00' && select.opening_end_hour === '00')
      }
    });
  }, []);

  // 드롭다운 외부 클릭 시 닫기 기능 
  const handleClickOutside = (event) => {
    if (rentalSelectRef.current && !rentalSelectRef.current.contains(event.target)) {
      setRentalState(prev => prev.isOpen ? { ...prev, isOpen: false } : prev);
    }
    if (returnSelectRef.current && !returnSelectRef.current.contains(event.target)) {
      setReturnState(prev => prev.isOpen ? { ...prev, isOpen: false } : prev);
    }
    if (bankSelectRef.current && !bankSelectRef.current.contains(event.target)) {
      setSelectBank(prev => prev.isOpen ? { ...prev, isOpen: false } : prev);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // 대여 날짜 변경 핸들러
  const handleRentalDateChange = useCallback((newDate) => {
    if (!newDate || !newDate.isValid()) return;
    
    // 대여 날짜 업데이트
    setRentalDateTime(prev => ({
      ...prev,
      date: newDate
    }));
    
    // 반납 날짜도 동일하게 업데이트
    setReturnDateTime(prev => ({
      ...prev,
      date: newDate
    }));
  }, []);

  // 대여 시간 변경 핸들러
  const handleRentalTimeChange = useCallback((newTime) => {
    if (!newTime) return;
    
    setRentalDateTime(prev => ({
      ...prev,
      time: newTime
    }));
  }, []); 
  
  // 반납 날짜 변경 핸들러
  const handleReturnDateChange = useCallback((newDate) => {
    if (!newDate || !newDate.isValid()) return;
    
    setReturnDateTime(prev => ({
      ...prev,
      date: newDate
    }));
  }, []); 

  // 반납 시간 변경 핸들러
  const handleReturnTimeChange = useCallback((newTime) => {
    if (!newTime) return;
    
    setReturnDateTime(prev => ({
      ...prev,
      time: newTime
    }));
  }, []); 
  
  // 패키지 보기 버튼
  const handleShow = (id) => {
    setIsShow(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // 상품 선택 핸들러 (견적서에서는 UI 상태만 관리)
  const handleProductSelect = useCallback((productId, isSelected, type = 'main') => {
    setCartListData(prevCart => ({
      bundleMain: prevCart.bundleMain?.map(item => ({
        ...item,
        isChecked: item.cart_option_seq === productId ? isSelected : (item.isChecked !== undefined ? item.isChecked : true)
      })) || [],
      bundleAdd: prevCart.bundleAdd?.map(item => ({
        ...item,
        isChecked: item.cart_option_seq === productId ? isSelected : (item.isChecked !== undefined ? item.isChecked : true)
      })) || [],
      bundleAcc: prevCart.bundleAcc?.map(item => ({
        ...item,
        isChecked: item.cart_option_seq === productId ? isSelected : (item.isChecked !== undefined ? item.isChecked : true)
      })) || []
    }));
  }, []);

  //요청사항
  const handleMemoChange = (e) => {
    setMemo(e.target.value);
  };

  // 테스트 일정과 촬영 시작 일정을 위한 상태 추가
  const [testDate, setTestDate] = useState(dayjs());
  const [shootingDate, setShootingDate] = useState(dayjs());

  // 테스트 일정과 촬영 시작 일정을 위한 핸들러
  const handleTestDateChange = useCallback((newDate) => {
    if (!newDate || !newDate.isValid()) return;
    setTestDate(newDate);
  }, []);

  const handleShootingDateChange = useCallback((newDate) => {
    if (!newDate || !newDate.isValid()) return;
    setShootingDate(newDate);
  }, []);

  // 대여지점 리스트
  useEffect(() => {
    const fetchStoreList = async () => {
      try {
        const response = await instance.post(STORE_LIST_URL);
        setStoreList(response.data.data)
      } catch (error) {
        console.error('대여지점 리스트 가져오기 실패:', error);
      } 
    };
    fetchStoreList();
  }, []);
  
  // 결제 방식 변경 핸들러
  const handlePaymentMethodChange = (e) => {
    const newPaymentMethod = e.target.value;
    setPaymentMethod(newPaymentMethod);
    
    // 분할 결제 선택 시 자동으로 계약금 체크
    if (newPaymentMethod === 'installment') {
      setPaymentDetails(prev => ({
        ...prev,
        request_deposit: true  // 계약금은 분할결제 시 디폴트로 체크
      }));
    }
  };

  // 분할 결제 옵션 체크박스 핸들러
  const handlePaymentDetailChange = (e) => {
    setPaymentDetails(prev => ({
      ...prev,
      [e.target.id]: e.target.checked
    }));
  };

  // 기존 state 선언부 아래에 추가
  const [projectType, setProjectType] = useState('');
  const [projectTypeEtc, setProjectTypeEtc] = useState('');

  // 프로젝트 타입 변경 핸들러
  const handleProjectTypeChange = (e) => {
    setProjectType(e.target.id);
    if (e.target.id !== 'etc') {
      setProjectTypeEtc('');
    }
  };

  // 기타 입력 핸들러
  const handleProjectTypeEtcChange = (e) => {
    setProjectTypeEtc(e.target.value);
  };

  // 견적서 제출 핸들러 수정
  const handleSubmit = () => {
    // 필수 정보 체크 
    if (!rentalState.selectValue) {
      setIsPopupLoginError(t('rental_point_selection_txt'));
      return;
    }
    
    if (!rentalDateTime.time) {
      setIsPopupLoginError(t('rental_time_selection_txt'));
      return;
    }
    
    // 반납 정보는 항상 필수로 체크
    if (!returnState.selectValue) {
      setIsPopupLoginError(t('return_point_selection_txt'));
      return;
    }
    
    if (!returnDateTime.time) {
      setIsPopupLoginError(t('return_time_selection_txt'));
      return;
    }
    
    // 분할 결제 선택 시 결제 옵션 체크
    if (paymentMethod === 'installment' && 
        !paymentDetails.request_deposit && 
        !paymentDetails.request_second_deposit && 
        !paymentDetails.request_last_pay) {
      setIsPopupLoginError(t('split_payment_selection_txt'));
      return;
    }
    
    // 약관 동의 체크
    if (!isAllChecked) {
      setIsPopupLoginError(t('agree_all_terms_txt'));
      return;
    }

    
    // 견적서 데이터 생성 (기존 코드 유지)
    const estimateData = {
      order_seq: "",
      reorder_chk: "N",
      order_type: "estimate",
      rental_store_seq: rentalState.selectValue,
      return_store_seq: returnState.selectValue,
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      download_seq: "",
      point: "",
      shipping_cost: "",
      balance: "",
      balance_cash_receipt: "N",
      balance_cash_num: "",
      
      depositInfo: {
        deposit_account: document.getElementById('account')?.value || "",
        deposit_mname: selectBank.bankItem || "",
        deposit_name: document.getElementById('account_holder')?.value || "",
        deposit_price: ""
      },
      
      insuranceInfo: {
        insurance_seq: "",
        insurance_repair_per: "",
        insurance_price: ""
      },
      
      discountInfo: {
        first_payment_sale: 0,
        mem_group_sale: 0,
        mem_type_sale: 0
      },
      
      estimateInfo: {
        undecidedChk: "N",
        email: myInfo?.email || "",
        phone: myInfo?.mem_phone || "",
        phone2: document.getElementById('phone2')?.value || "",
        business_name: document.getElementById('business_name')?.value || "",
        business_number: document.getElementById('business_number')?.value || "",
        project_manager_name: document.getElementById('project_manager_name')?.value || "",
        project_manager_phone: document.getElementById('project_manager_phone')?.value || "",
        project_director: document.getElementById('project_director')?.value || "",
        project_production: document.getElementById('project_production')?.value || "",
        project_test_date: testDate.format('YYYY-MM-DD'),
        project_start_date: shootingDate.format('YYYY-MM-DD'),
        project_name: document.getElementById('project_name')?.value || "",
        project_type: projectType,
        project_type_etc: projectType === 'etc' ? projectTypeEtc : '',
        project_content: document.getElementById('project_content')?.value || "",
        request_deposit_use: 
          paymentMethod === 'installment' && paymentDetails.request_deposit ? "Y" : "N",
        request_second_deposit_use: 
          paymentMethod === 'installment' && paymentDetails.request_second_deposit ? "Y" : "N",
        request_last_pay_use: 
          paymentMethod === 'installment' && paymentDetails.request_last_pay ? "Y" : "N"
      },
      
      user_memo: memo,
      pay_select_goods: quotationData?.selectedProducts?.ids || [],
      rental_payment_type: ""
    };
    
    
    // EstimatePopup 컴포넌트 수정을 위한 onConfirm 함수 추가
    const onConfirmSubmit = () => {
      // API 호출 변경: orderApi.getCartDetail 대신 PaymentService.requestPayment 사용
      PaymentService.requestPayment(estimateData)
        .then(response => {
          if (response.success) {
            // 성공 메시지를 표시하기 전에 임시 함수를 전역에 등록
            window.navigateToMain = () => {
              navigate(`/order/orderComplete/${response.data?.oid}`, {
                state: {
                  order_seq: response?.data?.oid,
                  settleprice: response.data?.settleprice,
                  order_type: "visit",
                }
              });
            };
            // 성공 메시지 설정 - 특별한 식별자를 포함
            setIsPopupLoginError({
              message: `견적서 문의가 완료되었습니다.<br>문의내역은 마이페이지에서<br>확인 가능합니다.`,
              action: 'navigate_main'
            });
          } else {
            // 실패 처리
            setIsPopupLoginError(response.message || "견적서 제출에 실패했습니다.");
          }
        })
        .catch(error => {
          console.error('견적서 제출 실패:', error);
          setIsPopupLoginError("견적서 제출 중 오류가 발생했습니다.");
        });
    };
    
    // onConfirm 함수를 EstimatePopup에 전달하기 위한 임시 저장소
    window.confirmSubmitEstimate = onConfirmSubmit;
    
    setIsPopup(t('apply_for_reservation_txt'));
  };

  // 상품 수량 변경 핸들러
  const handleProductQuantityChange = useCallback((changeData) => {
    // API 호출을 위한 파라미터 생성
    const selectedProductIds = quotationData?.selectedProducts?.ids || [];
    const params = {
      additional_sale: "",
      allBalanceUse: "N",
      allPointUse: "N",
      cart_change_goods: [changeData],
      cart_select_goods: "",
      deposit_price: "",
      download_seq: "",
      order_type: "visit",
      pay_select_goods: selectedProductIds.length > 0 ? selectedProductIds : [],
      point: "",
      rental_edate: returnDateTime.date 
                  ? `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_sdate: rentalDateTime.date 
                  ? `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_store_seq: rentalState.selectValue || "",
      return_store_seq: returnState.selectValue || "",
      undecidedChk: "N"
    };

    orderApi.getCartDetail(params)
      .then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          // 상품 데이터 업데이트
          setCartListData(normalizeCartList(data.cartList));
          // 주문 요약 정보 업데이트
          setOrderSummary(data);
        }
      })
      .catch(error => {
        console.error('상품 수량 변경 중 오류:', error);
      });
  }, [quotationData, rentalDateTime, returnDateTime, rentalState, returnState]);

  // 카운트버튼 핸들러 (아이템별로 호출됨)
  const handleCount = useCallback((item, type) => {
    // 체크되지 않은 상태라면 수량 변경 불가 (isChecked가 undefined면 기본적으로 체크된 것으로 간주)
    const isChecked = item.isChecked !== undefined ? item.isChecked : true;
    if (!isChecked) {
      setIsPopupLoginError('상품을 먼저 선택해주세요.');
      return;
    }
    
    // 현재 수량 가져오기
    let currentCount = parseInt(item.ea || 1, 10);
    
    // 수량 계산
    let newCount;
    if (type === 'plus') {
      newCount = currentCount + 1; 
    } else {
      newCount = Math.max(1, currentCount - 1); // 최소 1개 이상
    }
    
    // API 호출을 위한 데이터 준비
    const changeData = {
      cart_option_seq: item.cart_option_seq,
      ea: newCount,
      delete_flag: 'N'
    };
    
    handleProductQuantityChange(changeData);
  }, [handleProductQuantityChange]);

  // 상품 삭제 핸들러
  const handleProductDelete = useCallback((deleteData) => {
    // 삭제될 상품 ID
    const deletedProductId = deleteData.cart_option_seq;

    // UI에서 먼저 삭제 (즉시 삭제 효과를 보여주기 위함)
    setCartListData(prev => ({
      bundleMain: prev.bundleMain?.filter(item => item.cart_option_seq !== deletedProductId) || [],
      bundleAdd: prev.bundleAdd?.filter(item => item.cart_option_seq !== deletedProductId) || [],
      bundleAcc: prev.bundleAcc?.filter(item => item.cart_option_seq !== deletedProductId) || []
    }));

    // API 호출을 위한 파라미터 생성
    const selectedProductIds = quotationData?.selectedProducts?.ids || [];
    const updatedProductIds = selectedProductIds.filter(id => id !== deletedProductId);
    
    const params = {
      additional_sale: "",
      allBalanceUse: "N",
      allPointUse: "N",
      cart_change_goods: [deleteData],
      cart_select_goods: "",
      deposit_price: "",
      download_seq: "",
      order_type: "visit",
      pay_select_goods: updatedProductIds.length > 0 ? updatedProductIds : [],
      point: "",
      rental_edate: returnDateTime.date 
                  ? `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_sdate: rentalDateTime.date 
                  ? `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_store_seq: rentalState.selectValue || "",
      return_store_seq: returnState.selectValue || "",
      undecidedChk: "N"
    };

    // API 호출
    orderApi.getCartDetail(params)
      .then(response => {
        // 장바구니가 비어있는 경우 체크
        const isEmpty = response.data.code === -200 ||
          (response.data.code === 200 &&
           (!response.data.data.cartList ||
            Object.keys(response.data.data.cartList).length === 0 ||
            ((!response.data.data.cartList.bundleMain?.length) &&
             (!response.data.data.cartList.bundleAdd?.length) &&
             (!response.data.data.cartList.bundleAcc?.length))));
        
        if (isEmpty) {
          // 장바구니가 비었으므로 모든 데이터 초기화
          setCartListData({
            bundleMain: [],
            bundleAdd: [],
            bundleAcc: []
          });
          setOrderSummary({...response.data.data, cartList: {bundleMain: [], bundleAdd: [], bundleAcc: []}});
          return;
        }

        if (response.data.code === 200) {
          const data = response.data.data;

          // 서버 데이터로 상태 업데이트
          setCartListData(normalizeCartList(data.cartList));

          // 주문 요약 정보 업데이트
          setOrderSummary(data);
        }
      })
      .catch(error => {
        console.error('상품 삭제 중 오류:', error);
      });
  }, [quotationData, rentalDateTime, returnDateTime, rentalState, returnState]);

  // 삭제 버튼 핸들러 (아이템별로 호출됨)
  const handleDelete = useCallback((item) => {
    if (!item?.cart_option_seq) return;
    
    const deleteData = {
      cart_option_seq: item.cart_option_seq,
      ea: item.ea || 1, // ea가 없으면 기본값 1 사용
      delete_flag: 'Y'
    };
    
    handleProductDelete(deleteData);
  }, [handleProductDelete]);

  // 대여상품만 삭제하는 핸들러
  const handleMainProductsDelete = useCallback(() => {
    // 대여상품(bundleMain) 중에서 선택된 항목만 필터링
    const mainProductIds = cartListData.bundleMain
      .filter(item => {
        const isChecked = item.isChecked !== undefined ? item.isChecked : true;
        return isChecked;
      })
      .map(item => item.cart_option_seq);

    if (mainProductIds.length === 0) {
      setIsPopupLoginError('삭제할 대여상품을 선택해주세요.');
      return;
    }

    // UI에서 먼저 삭제 (즉시 삭제 효과를 보여주기 위함)
    setCartListData(prev => ({
      bundleMain: prev.bundleMain?.filter(item => !mainProductIds.includes(item.cart_option_seq)) || [],
      bundleAdd: prev.bundleAdd || [],
      bundleAcc: prev.bundleAcc || []
    }));

    // cartListData에서 모든 상품을 하나의 배열로 합치기 (삭제 전 데이터 사용)
    const allProducts = [
      ...(cartListData.bundleMain || []),
      ...(cartListData.bundleAdd || []),
      ...(cartListData.bundleAcc || [])
    ];

    const deleteList = mainProductIds.map(id => {
      // cartListData에서 해당 cart_option_seq를 가진 상품 찾기
      const product = allProducts.find(item => item.cart_option_seq === id);
      // 상품을 찾으면 해당 ea 값 사용, 없으면 1 (기본값)
      const ea = product?.ea || 1;
      
      return {
        cart_option_seq: id,
        ea: ea,
        delete_flag: 'Y'
      };
    });

    // API 호출을 위한 파라미터 생성
    const selectedProductIds = quotationData?.selectedProducts?.ids || [];
    const updatedProductIds = selectedProductIds.filter(id => !mainProductIds.includes(id));
    
    const params = {
      additional_sale: "",
      allBalanceUse: "N",
      allPointUse: "N",
      cart_change_goods: deleteList,
      cart_select_goods: "",
      deposit_price: "",
      download_seq: "",
      order_type: "visit",
      pay_select_goods: updatedProductIds.length > 0 ? updatedProductIds : [],
      point: "",
      rental_edate: returnDateTime.date 
                  ? `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_sdate: rentalDateTime.date 
                  ? `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_store_seq: rentalState.selectValue || "",
      return_store_seq: returnState.selectValue || "",
      undecidedChk: "N"
    };

    // API 호출
    orderApi.getCartDetail(params)
      .then(response => {
        // 장바구니가 비어있는 경우 체크
        const isEmpty = response.data.code === -200 ||
          (response.data.code === 200 &&
           (!response.data.data.cartList ||
            Object.keys(response.data.data.cartList).length === 0 ||
            ((!response.data.data.cartList.bundleMain?.length) &&
             (!response.data.data.cartList.bundleAdd?.length) &&
             (!response.data.data.cartList.bundleAcc?.length))));
        
        if (isEmpty) {
          // 장바구니가 비었으므로 모든 데이터 초기화
          setCartListData({
            bundleMain: [],
            bundleAdd: [],
            bundleAcc: []
          });
          setOrderSummary({...response.data.data, cartList: {bundleMain: [], bundleAdd: [], bundleAcc: []}});
          return;
        }

        if (response.data.code === 200) {
          const data = response.data.data;

          // 서버 데이터로 상태 업데이트
          setCartListData(normalizeCartList(data.cartList));

          // 주문 요약 정보 업데이트
          setOrderSummary(data);
        }
      })
      .catch(error => {
        console.error('대여상품 삭제 실패:', error);
      });
  }, [cartListData, quotationData, rentalDateTime, returnDateTime, rentalState, returnState]);

  // 액세서리 삭제 핸들러
  const handleAccProductsDelete = useCallback(() => {
    // 액세서리(bundleAcc) 중에서 선택된 항목만 필터링
    const accProductIds = cartListData.bundleAcc
      .filter(item => {
        const isChecked = item.isChecked !== undefined ? item.isChecked : true;
        return isChecked;
      })
      .map(item => item.cart_option_seq);

    if (accProductIds.length === 0) {
      setIsPopupLoginError('삭제할 액세서리를 선택해주세요.');
      return;
    }

    // UI에서 먼저 삭제 (즉시 삭제 효과를 보여주기 위함)
    setCartListData(prev => ({
      bundleMain: prev.bundleMain || [],
      bundleAdd: prev.bundleAdd || [],
      bundleAcc: prev.bundleAcc?.filter(item => !accProductIds.includes(item.cart_option_seq)) || []
    }));

    const deleteList = accProductIds.map(id => ({
      cart_option_seq: id,
      ea: 0,
      delete_flag: 'Y'
    }));

    // API 호출을 위한 파라미터 생성
    const selectedProductIds = quotationData?.selectedProducts?.ids || [];
    const updatedProductIds = selectedProductIds.filter(id => !accProductIds.includes(id));
    
    const params = {
      additional_sale: "",
      allBalanceUse: "N",
      allPointUse: "N",
      cart_change_goods: deleteList,
      cart_select_goods: "",
      deposit_price: "",
      download_seq: "",
      order_type: "visit",
      pay_select_goods: updatedProductIds.length > 0 ? updatedProductIds : [],
      point: "",
      rental_edate: returnDateTime.date 
                  ? `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_sdate: rentalDateTime.date 
                  ? `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`
                  : "",
      rental_store_seq: rentalState.selectValue || "",
      return_store_seq: returnState.selectValue || "",
      undecidedChk: "N"
    };

    // API 호출
    orderApi.getCartDetail(params)
      .then(response => {
        // 장바구니가 비어있는 경우 체크
        const isEmpty = response.data.code === -200 ||
          (response.data.code === 200 &&
           (!response.data.data.cartList ||
            Object.keys(response.data.data.cartList).length === 0 ||
            ((!response.data.data.cartList.bundleMain?.length) &&
             (!response.data.data.cartList.bundleAdd?.length) &&
             (!response.data.data.cartList.bundleAcc?.length))));
        
        if (isEmpty) {
          // 장바구니가 비었으므로 모든 데이터 초기화
          setCartListData({
            bundleMain: [],
            bundleAdd: [],
            bundleAcc: []
          });
          setOrderSummary({...response.data.data, cartList: {bundleMain: [], bundleAdd: [], bundleAcc: []}});
          return;
        }

        if (response.data.code === 200) {
          const data = response.data.data;

          // 서버 데이터로 상태 업데이트
          setCartListData(normalizeCartList(data.cartList));

          // 주문 요약 정보 업데이트
          setOrderSummary(data);
        }
      })
      .catch(error => {
        console.error('액세서리 삭제 실패:', error);
      });
  }, [cartListData, quotationData, rentalDateTime, returnDateTime, rentalState, returnState]);

  // 팝업 상태 추가
  const [isTermsPopup, setIsTermsPopup] = useState(false);
  const [popupContent, setPopupContent] = useState({ title: '', content: '' });
  
  // 약관 보기 버튼 클릭 핸들러 수정
  const handleViewTerms = async (type, event) => {
    event.stopPropagation(); // 이벤트 전파 중지
    event.preventDefault(); // 기본 동작 방지
    
    let title = '';
    let dataKey = '';

    switch(type) {
      case 'order_product_agreement':
        title = '주문 상품에 동의';
        dataKey = 'order_product_agreement';
        break;
      case 'privacy_use_agreement':
        title = '개인정보 수집/이용 동의';
        dataKey = 'privacy_use_agreement';
        break;
      case 'privacy_3rd_agreement':
        title = '개인정보 제3자 제공 동의';
        dataKey = 'privacy_3rd_agreement';
        break;
      case 'pg_service_agreement':
          title = '';
          dataKey = 'pg_service_agreement';
          break;
      default:
        return;
    }

    try {
      const response = await instance.post(TERMS_LIST_URL);
      
      if (response.data.code === 200) {
        setPopupContent({ 
          title, 
          content: response.data.data[dataKey] || '약관 내용을 불러올 수 없습니다.' 
        });
        setIsTermsPopup(true);
      } else {
        throw new Error(response.data.message || '약관 내용을 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('약관 내용을 불러오는 중 오류 발생:', error);
      setPopupContent({ 
        title, 
        content: '약관 내용을 불러올 수 없습니다. 잠시 후 다시 시도해주세요.' 
      });
      setIsTermsPopup(true);
    }
  };

  return(
    <div className="order_page"> 
      <div className="order_inner quotation_page">
        <h3 className="quo_title">{t('quotation_title')}</h3>

        {/* 주문 정보 */}
        <section style={{marginTop:'40px'}}>
          <div className="order_title">
            <div className="flex-8"> 
              <h3>{t('order_info')}</h3>
            </div>
            <div style={{font:'var(--body1-1)'}}>
              <span className="reset_text_btn">* </span>{t('required_fields')}
            </div>
          </div>
          <ul className="default_info_table addr_table">
            {/* 대여정보 */}
            <li className="info_item quo_rental">
              <div className="label">
                <p>{t('rental_info')} <span className="yellow">*</span></p>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="select_info quo">

                    {/* 대여 지점 */}
                    <div
                      ref={rentalSelectRef}
                      className={`default_select border ${rentalState.isOpen ? 'open' : ''}`}
                    >
                      <button
                        className={`select_btn ${rentalState.selectValue ? 'active' : ''}`}
                        type="button"
                        onClick={handleRentalSelectOpen}
                      >
                        <span className="ellipsis_clamp">
                          {rentalState.selectItem || t('rental_point_selection')}
                        </span>
                      </button>
                      <ul className="select_list">
                        {storeList.map((select, index) => (
                          <li
                            key={index}
                            className={`select_item ellipsis1 ${
                              rentalState.selectValue === select.store_seq ? 'active' : ''
                            }`}
                            onClick={() => handleRentalSelectItem(select)}
                          >
                            {select.store_name}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* 대여 일시 */}
                    <DatePicker
                      value={rentalDateTime.date}
                      onDateChange={handleRentalDateChange}
                      minDate={dayjs()}
                      isDisabled={false}
                    />
                    <TimePicker
                      value={rentalDateTime.time}
                      onTimeChange={handleRentalTimeChange}
                      isDisabled={!rentalState.storeInfo}
                      storeInfo={rentalState.storeInfo}
                      compareDate={rentalDateTime.date}
                      isTodayCheck={true}
                    />
                  </div>
                </div>
              </div>
            </li>

            {/* 반납정보 */}
            <li className="info_item quo_return">
              <div className="label">
                <label htmlFor="receiver">
                  <p>{t('return_info')} <span className="yellow">*</span></p>
                </label>
              </div>
              <div className="form_info">
                <div className="info_detail gap_16">
                  <div className="select_info quo">
                    {/* 반납 지점 선택 */}
                    <div
                      ref={returnSelectRef}
                      className={`default_select border ${returnState.isOpen ? 'open' : ''}`}
                    >
                      <button
                        className={`select_btn ${returnState.selectValue ? 'active' : ''}`}
                        type="button"
                        onClick={handleReturnSelectOpen}
                      >
                        <span className="ellipsis_clamp">
                          {returnState.selectItem ? (
                            <span className="select_store">{returnState.selectItem}</span>
                          ) : (
                            <span>{t('select_return_branch')}</span>
                          )}
                        </span>
                      </button>
                      <ul className="select_list">
                        {storeList.map((select, index) => (
                          <li
                            key={index}
                            className={`select_item ellipsis1 ${
                              returnState.selectValue === select.store_seq ? 'active' : ''
                            }`}
                            onClick={() => handleReturnSelectItem(select)}
                          >
                            {select.store_name}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 반납 일시 */}
                      <DatePicker
                        value={returnDateTime.date}
                        onDateChange={handleReturnDateChange}
                        minDate={rentalDateTime.date}
                        isDisabled={false}
                      />
                      <TimePicker
                        value={returnDateTime.time}
                        onTimeChange={handleReturnTimeChange}
                        isDisabled={!returnState.storeInfo}
                        storeInfo={returnState.storeInfo}
                        compareDate={returnDateTime.date}
                        compareTime={rentalDateTime.time}
                        isReturn={true}
                        isTodayCheck={true}
                        rentalDateTime={rentalDateTime}
                      />
                  </div>
                  <div>

                  {/* <div className="default_chk_box">
                    <input
                      type="checkbox"
                      id="extend"
                      checked={isUndecided}
                      onChange={handleUndecidedCheck}
                    />
                    <label htmlFor="extend">
                      <span>{t('return_undecided_or_longterm')}</span>
                    </label>
                  </div> */}
                  </div> 
                </div>
              </div>
            </li>
          </ul>
        </section>


        {/* 주문자 정보 */}
        <section className="mt_48">
          <div className="order_title">
              <h3>{t('orderer_info')}</h3>
          </div>
          <ul className="default_info_table quote_table">
            <li className="info_item">
              <div className="label">
                <label htmlFor="name">{t('name')} <i className="yellow">*</i></label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  className="readonly"
                  id="name"
                  readOnly
                  value={myInfo?.user_name || ""}
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="email">{t('email')} <i className="yellow">*</i></label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  className="readonly"
                  id="email"
                  readOnly
                  value={myInfo?.email || ""}
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="phone">{t('mobile_number')} <i className="yellow">*</i></label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  className="readonly"
                  id="phone"
                  readOnly
                  value={formatPhoneNumber(myInfo?.mem_phone || "")}
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="phone2">{t('alternate_contact')} <i className="yellow">*</i></label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  className="readonly"
                  id="phone2"
                  readOnly
                  value={formatPhoneNumber(myInfo?.mem_phone2 || "")}
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="business_name">{t('business_name')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  id="business_name"
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="ceo">{t('ceo')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  id="ceo"
                />
              </div>
            </li>
            <li className="info_item">
              <div className="label">
                <label htmlFor="business_number">{t('business_registration_number')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="number"
                  id="business_number"
                />
              </div>
            </li>
            <li className="info_item">
              {/* 빈칸 */}
            </li>
          </ul>
        </section>

        {/* 프로그램 정보 */} 
        <section className="mt_48 quo_pro quo_pro_info">
          <div className="order_title">
              <h3>{t('program_info_txt')}</h3>
          </div>
          <ul className="default_info_table quote_table">
            <li className="info_item">
              <div className="label">
                <label htmlFor="project_manager_name">{t('person_in_charge')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  id="project_manager_name"
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="project_manager_phone">{t('contact')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="number"
                  id="project_manager_phone"
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="project_director">{t('cinematographer')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  id="project_director"
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="project_production">{t('production_company')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  id="project_production"
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <span>{t('test_schedule')}</span>
              </div>
              <div className="form_info">
              <DatePicker
                value={testDate}
                onDateChange={handleTestDateChange}
                isDisabled={false}
              />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <span>{t('shooting_start_schedule')}</span>
              </div>
              <div className="form_info">
              <DatePicker
                value={shootingDate}
                onDateChange={handleShootingDateChange}
                isDisabled={false}
              />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="project_name">{t('program_name')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  id="project_name"
                />
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="project_type">{t('program_type')}</label>
              </div>
              <div className="form_info default_input">
                <div className="default_radio radio_list">
                  <div>
                    <input
                      type="radio"
                      name="project_type"
                      id="drama"
                      checked={projectType === 'drama'}
                      onChange={handleProjectTypeChange}
                    />
                    <label htmlFor="drama">{t('drama_txt')}</label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="project_type"
                      id="movie"
                      checked={projectType === 'movie'}
                      onChange={handleProjectTypeChange}
                    />
                    <label htmlFor="movie">{t('movie_txt')}</label>
                  </div>
                  <div className="flex-12">
                    <input
                      type="radio"
                      name="project_type"
                      id="etc"
                      checked={projectType === 'etc'}
                      onChange={handleProjectTypeChange}
                    />
                    <label htmlFor="etc">{t('etc')}</label>
                    <input
                      type="text"
                      id="project_type_etc"
                      value={projectTypeEtc}
                      onChange={handleProjectTypeEtcChange}
                      disabled={projectType !== 'etc'}
                      style={{display: 'block'}}
                    />
                  </div>
                </div>
              </div>
            </li>

            <li className="info_item">
              <div className="label">
                <label htmlFor="project_content">{t('work_content')}</label>
              </div>
              <div className="form_info default_input">
                <input
                  type="text"
                  id="project_content"
                />
              </div>
            </li>
          </ul>
        </section>

        {/* 결제 정보 */}
        <section className="mt_48 quo_pro quo_info">
          <div className="order_title">
            <h3>{t('payment_information')}</h3>
          </div>
          <div className="default_info_table payment">
            {/* 예상 결제금액 */}
            <div className="info_item total_order_price">
              <div className="label">
                <span>{t('expected_payment_amount')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail nowrap pd_16 quo_right">
                {formatLocalString(selectedSummary.totalPrice)}{t('price_txt')}
                </div>
              </div>
            </div>

            {/* 결제방식 */}
            <div className="info_item quo_payment_methode">
              <div className="label">
                <span>{t('payment_method')}</span>
              </div>
              <div className="form_info default_input">
                <div className="info_detail">
                  <div className="default_radio radio_list" style={{margin:0}}>
                    <div>
                      <input
                        type="radio"
                        name="payment"
                        id="lump_sum"
                        value="lump_sum"
                        checked={paymentMethod === 'lump_sum'}
                        onChange={handlePaymentMethodChange}
                      />
                      <label htmlFor="lump_sum">{t('one_time_payment')}</label>
                    </div>
                    <div>
                      <input
                        type="radio"
                        name="payment"
                        id="installment"
                        value="installment"
                        checked={paymentMethod === 'installment'}
                        onChange={handlePaymentMethodChange}
                      />
                      <label htmlFor="installment">{t('installment_payment')}</label>
                    </div>
                  </div>
                  
                  {/* 분할결제 선택 시에만 표시 */}
                  {paymentMethod === 'installment' && (
                    <div className="case_none">
                      <div className="flex-12">
                        <div className="default_chk_box">
                          <input 
                            type="checkbox"
                            id="request_deposit"
                            checked={paymentDetails.request_deposit}
                            onChange={handlePaymentDetailChange}
                          />
                          <label htmlFor="request_deposit">
                            <span>{t('down_payment')}</span>
                          </label>
                        </div>
                        <div className="default_chk_box">
                          <input 
                            type="checkbox"
                            id="request_second_deposit"
                            checked={paymentDetails.request_second_deposit}
                            onChange={handlePaymentDetailChange}
                          />
                          <label htmlFor="request_second_deposit">
                            <span>{t('interim_payment')}</span>
                          </label>
                        </div>
                        <div className="default_chk_box">
                          <input 
                            type="checkbox"
                            id="request_last_pay"
                            checked={paymentDetails.request_last_pay}
                            onChange={handlePaymentDetailChange}
                          />
                          <label htmlFor="request_last_pay">
                            <span>{t('balance_payment')}</span>
                          </label>
                        </div>
                      </div>
                      <span className="dash_list guide_text_1 g-80">
                        {t('installment_payment_notice')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 대여상품 정보 */}
        <section className="mt_48 check_pd">
          <div className="order_title">
            <div className="flex-8">
              <h3>{t('rental_product_info')}</h3>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleMainProductsDelete}
              >
                {t("delete_selected_product")}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleMain && cartListData.bundleMain.length > 0 ? (
                cartListData?.bundleMain?.map((item, index) => (
                  <li 
                    key={item.cart_option_seq || index}
                    style={{
                      borderTop: index > 0 ? '1px solid black' : 'none'
                    }}
                  >
                    <div className="order_item default_prod">
                      <div className="default_chk_box">
                        <input 
                          type="checkbox" 
                          id={`goods_${item?.cart_option_seq}`}
                          checked={item?.isChecked !== undefined ? item.isChecked : true}
                          onChange={(e) => handleProductSelect(item.cart_option_seq, e.target.checked, 'main')}
                        />
                        <label htmlFor={`goods_${item?.cart_option_seq}`} />
                      </div>
                      <div className="prod_container">
                        <img
                          src={item.goods_image || product}
                          alt="상품 이미지"
                          className="order_prod_img"
                          loading="lazy"
                        />
                        <div className="prod_info">
                          <div className="info_box">
                            <div className="prod_title">
                              <p className="title ellipsis1">{item?.product_name || '상품명'}</p>
                              <p className="sub_title ellipsis1">{item?.description_kr_01 || '부제목'}</p>
                              {/* <p className="option_count">{t('package')}: {item?.package_name|| '기본 패키지'}</p> */}
                              {orderSummary !== 'sale' && item?.package_name && item?.package_name !== '' && (
                                <p className="option_count desktop-only">
                                  {t('package')}: {item?.package_name}
                                </p>
                              )}
                              {/* <p className="strong_b3" style={{marginTop:'8px'}}>₩<span className="strong_b1">{item?.sum_sale_price ? formatLocalString(item?.sum_sale_price || 0) : formatLocalString(item?.price || 0)}</span></p> */}
                            </div>

                            <div className="insurance_box">
                              {item?.insurance_flag === 'N' && (
                                <p>보험 미가입상품</p>
                              )}
                            </div>
                            
                            <div className="price">
                              {orderSummary === 'sale' ? (
                                <p>₩<span>{item?.price?.toLocaleString()}</span></p>
                              ) : (
                                <p>₩<span>{item?.unit_rental_12_price?.toLocaleString()}</span> / ₩<span>{item?.unit_rental_24_price?.toLocaleString()}</span></p>

                              )}
                            </div>
                          </div>
                          <div className="prod_count_box">
                            <div className="count_btn_box">
                              <button
                                type="button"
                                className="minus_btn"
                                onClick={() => handleCount(item, 'minus')}
                                disabled={item.isChecked !== undefined ? !item.isChecked : false}
                              />
                              <input
                                type="number"
                                value={item.ea || 1}
                                readOnly
                                  style={{ opacity: (item.isChecked !== undefined ? item.isChecked : true) ? 1 : 0.5 }}
                              />
                              <button
                                type="button"
                                className="plus_btn"
                                onClick={() => handleCount(item, 'plus')}
                                disabled={item.isChecked !== undefined ? !item.isChecked : false}
                              />
                            </div>
                            {item?.stock_flag === 'N' && (  // 재고부족
                              <div className="stock_btn">
                                <span className="mobile-text">{t('stock_shortage')}</span>
                              </div>
                            )}
                          </div>
                          <div className="total_price">
                            <p>
                              <span className="won">₩</span>
                              <span className="order_amount">
                                {item?.sum_sale_price?.toLocaleString()}
                              </span>
                            </p>
                            <button 
                              type="button" 
                              className="delete_btn"
                              onClick={() => handleDelete(item)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* bundlePkg 데이터가 있을 경우 표시 */}
                    {item?.bundlePkg && item?.bundlePkg.length > 0 && (
                      <div className={`package_con ${isShow[item?.cart_option_seq] ? 'active' : ''}`}
                          onClick={() => handleShow(item?.cart_option_seq)}>
                        <div className="package_total flex-justify-center">
                          <div className="flex-center-2">
                          {t('package')}<span className="strong_ch">{item.bundlePkg.length}</span>{t('quantity_count_txt')}<i className="arrow_icon"></i>
                          </div>
                        </div>
                        {item?.bundlePkg.map((pkgItem, pkgIndex) => (
                          <div key={pkgItem?.cart_option_seq || pkgIndex} className="package_item">
                            <div className="default_chk_box"></div>
                            <div className="prod_container">
                              <img
                                src={pkgItem?.goods_image || product}
                                alt="상품 이미지"
                                className="order_prod_img"
                                loading="lazy"
                              />
                              <div className="prod_info">
                                <div className="info_box">
                                  <div className="prod_title">
                                    <p className="title ellipsis1">{pkgItem?.product_name || '패키지 상품명'}</p>
                                    <p className="sub_title ellipsis1">{pkgItem?.description_kr_01 || '패키지 부제목'}</p>
                                  </div>
                                </div>

                                <div className="prod_count_box">
                                  <div>{/* 빈박스 */}</div>
                                  {pkgItem?.stock_flag === 'N' && (
                                    <div className="stock_btn">
                                      <span className="mobile-text">{t('stock_shortage')}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="total_price">
                                  <button
                                    type="button"
                                    className="delete_btn"
                                    onClick={() => handleDelete(pkgItem)}
                                  ></button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {item?.bundleCp && item.bundleCp.length > 0 && (
                      <div className={`package_con ${isShow[item?.cart_option_seq] ? 'active' : ''}`}
                          onClick={() => handleShow(item?.cart_option_seq)}>
                        <div className="package_total flex-justify-center">
                          <div className="flex-center-2">
                          구성품<span className="strong_ch">{item?.bundleCp.length}</span>{t('quantity_count_txt')}<i className="arrow_icon"></i>
                          </div>
                        </div>
                        {item?.bundleCp.map((CpItem, pkgIndex) => (
                          <div key={CpItem?.cart_option_seq || pkgIndex} className="package_item">
                            <div className="default_chk_box"></div>
                            <div className="prod_container">
                              <img
                                src={CpItem?.goods_image || product}
                                alt="상품 이미지"
                                className="order_prod_img"
                                loading="lazy"
                              />
                              <div className="prod_info">
                                <div className="info_box">
                                  <div className="prod_title">
                                    <p className="title ellipsis1">{CpItem?.product_name || '패키지 상품명'}</p>
                                    <p className="sub_title ellipsis1">{CpItem?.description_kr_01 || '패키지 부제목'}</p>
                                  </div>
                                </div>

                                <div className="prod_count_box">
                                  <div>{/* 빈박스 */}</div>
                                  {CpItem?.stock_flag === 'N' && (
                                    <div className="stock_btn">
                                      <span className="mobile-text">{t('stock_shortage')}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="total_price">
                                  <button
                                    type="button"
                                    className="delete_btn"
                                    onClick={() => handleDelete(CpItem)}
                                  ></button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))
              ) : (
                <div className="no_result">
                  <p>{t("no_rental_product_0617")}</p>
                </div>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            <div className="total_product flex-center-8"  style={{flexDirection:'column', alignItems:'end'}}>
              <div className="flex-center-8">
                <div className="flex-center-2">
                  {t('total_product_quantity2')} 
                  <p><span className="strong_b1">{selectedSummary.totalRentalEA}</span>{t('find_email_0617')}</p>
                </div>
                <div className="flex-center-2">
                  {t('total_product_quantity4_0617')} 
                  <p className="cap2_1">₩<span className="strong_b1">
                    {formatLocalString(selectedSummary.totalRentalPrice)}
                  </span></p>
                </div>
              </div>
                <span className="text_small_b g-80">{t("payment_final_amount_notice")}</span>
            </div>
          </div>
        </section>

        {/* 무상품목 정보 */}
        <section className="mt_48 check_pd">
          <div className="order_title">
            <div className="flex-8">
              <h3>{t('accessory_info_txt')}</h3>
              <p className="count">{(cartListData?.bundleAcc || []).filter(item => item.isChecked !== undefined ? item.isChecked : true).length}<span style={{color:"#8d8d8d"}}>/{cartListData?.bundleAcc?.length || 0}</span></p>
            </div>
            <div className="o_btn">
              <button
                type="button"
                className="default_btn black xs"
                onClick={handleAccProductsDelete}
              >
                {t("delete_selected_product")}
              </button>
            </div>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleAcc && cartListData.bundleAcc.length > 0 ? (
                cartListData.bundleAcc.map((item, index) => (
                <li key={index}>
                  <div className="order_item default_prod">
                    <div className="default_chk_box">
                      <input 
                        type="checkbox" 
                        id={`goods_${item?.cart_option_seq}`}
                        checked={item?.isChecked !== undefined ? item.isChecked : true}
                        onChange={(e) => handleProductSelect(item.cart_option_seq, e.target.checked, 'acc')}
                      />
                      <label htmlFor={`goods_${item?.cart_option_seq}`} />
                    </div>
                    <div className="prod_container">
                      <img
                        src={item.goods_image || product}
                        alt="상품 이미지"
                        className="order_prod_img"
                        loading="lazy"
                      />
                      <div className="prod_info">
                        <div className="info_box">
                          <div className="prod_title">
                            <p className="title ellipsis1">{item.product_name}</p>
                            <p className="quo_count">{t('quantity_txt')} :{item.ea}{t('quantity_count_txt')}</p>
                            {/* <p className="strong_b3" style={{marginTop:'8px'}}>₩<span className="strong_b1">{formatLocalString(item.price || 0)}</span></p> */}
                          </div>
                          <div className="price">
                              {orderSummary === 'sale' ? (
                                <p>₩<span>{item?.price?.toLocaleString()}</span></p>
                              ) : (
                                <p>₩<span>{item?.unit_rental_12_price?.toLocaleString()}</span> / ₩<span>{item?.unit_rental_24_price?.toLocaleString()}</span></p>

                              )}
                            </div>
                          </div>
                          <div className="prod_count_box">
                            <div className="count_btn_box">
                              <button
                                type="button"
                                className="minus_btn"
                                onClick={() => handleCount(item, 'minus')}
                                disabled={item.isChecked !== undefined ? !item.isChecked : false}
                              />
                              <input
                                type="number"
                                value={item.ea || 1}
                                readOnly
                                  style={{ opacity: (item.isChecked !== undefined ? item.isChecked : true) ? 1 : 0.5 }}
                              />
                              <button
                                type="button"
                                className="plus_btn"
                                onClick={() => handleCount(item, 'plus')}
                                disabled={item.isChecked !== undefined ? !item.isChecked : false}
                              />
                            </div>
                            {item?.stock_flag === 'N' && (  // 재고부족
                              <div className="stock_btn">
                                <span className="mobile-text">{t('stock_shortage')}</span>
                              </div>
                            )}
                          </div>
                          <div className="total_price">
                            <p>
                              <span className="won">₩</span>
                              <span className="order_amount">
                                {item?.sum_sale_price?.toLocaleString()}
                              </span>
                            </p>
                            <button 
                              type="button" 
                              className="delete_btn"
                              onClick={() => handleDelete(item)}
                            />
                          </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))
              ) : (
                <li>
                  <div className="order_item default_prod">
                    <div className="prod_container">
                      <div className="prod_info">
                        <div className="info_box">
                          <div className="prod_title">
                            <p className="title ellipsis1">등록된 무상품목이 없습니다.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              )}
            </ul>

            {/* 총 상품 수량, 상품 금액 */}
            {cartListData?.bundleAcc && cartListData.bundleAcc.length > 0 && (
              <div className="total_product flex-center-8">
                <div className="flex-center-2">
                  {t('total_product_quantity2')} 
                <p><span className="strong_b1">{selectedSummary.totalAccEA}</span>{t('quantity_count_txt')}</p>
                </div>
              </div>
            )}
          </div>
        </section> 

        {/* 요청사항 */}
        <section className="mt_48 container request_input quo_req">
          <div className="order_title">
            <h3>{t('request_txt')}</h3>
            <p className="text_small_b">{t('need_help_0617')}
              <span className="strong_b3 black">02-1544-2338</span>
              {t('need_help_0617_2')}
            </p>
          </div>
          <div className="default_input">
            <textarea 
              className="form_input"
              value={memo}
              onChange={handleMemoChange}
              placeholder={t('request_placeholder')}
            />
          </div>
          <p className="guide_text_1 black quo_request">
            {t('apply_for_rental_txt')}
          </p>
        </section>             

      </div>


      {/* 결제정보표시 */}
      <div className="total_payment_con">
        <div className="order_info_box">
          <div className="order_left">
            <div className="top_con flex-8 flex-start">
              <i className="p_icon">P</i>
              <p className="point_info">
                <span>
                  {t('normal_rental_and_return_txt')} {orderSummary?.pointSetting?.payStar1}%(₩{orderSummary?.pointSetting?.payStarMin ? formatLocalString(orderSummary?.pointSetting?.payStarMin) : '0'}) ~ {orderSummary?.pointSetting?.payStar5}%(₩{orderSummary?.pointSetting?.payStarMax ? formatLocalString(orderSummary?.pointSetting?.payStarMax) : '0'}) {t('point_txt')}
                </span>
                <button
                  className="text_small g-80"
                  type="button"
                  onClick={handleEquipmentPopup}
                >
                  {t('equipment_rating_info')}
                </button>
              </p>
            </div>
            <div className="bottom_con">
              <div className="join_con">
                <div className="default_chk_box all_chk">
                  <input 
                    type="checkbox" 
                    id="allChk" 
                    checked={isAllChecked}
                    onChange={handleAllCheck}
                  />
                  <label htmlFor="allChk">
                    <span>{t('order_info_check_0617')}</span>
                  </label>
                </div>  
                
                <ul className="flex-2 column g-90">
                  <li className="default_chk_box">
                    <input 
                      type="checkbox"
                      id="useAgree1"  
                      name="option1"
                      checked={isChecked.option1}
                      onChange={() => handleCheck('option1')}
                    />
                    <label htmlFor="useAgree1">
                      <span className="flex-btw">
                        {t('order_product_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('order_product_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox"
                      id="useAgree2"  
                      name="option2"
                      checked={isChecked.option2}
                      onChange={() => handleCheck('option2')}
                    />
                    <label htmlFor="useAgree2">
                      <span className="flex-btw">
                        {t('order_personal_info_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('privacy_use_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox" 
                      id="personalAgree3" 
                      name="option3"
                      checked={isChecked.option3}
                      onChange={() => handleCheck('option3')}
                    />
                    <label htmlFor="personalAgree3">
                      <span className="flex-btw">
                        {t('order_personal_info_agree_0617_2')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('privacy_3rd_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox" 
                      id="personalAgree4" 
                      name="option4"
                      checked={isChecked.option4}
                      onChange={() => handleCheck('option4')}
                    />
                    <label htmlFor="personalAgree4">
                      <span className="flex-btw">
                        {t('order_payment_service_agree_0617')}
                        <div className="g-70" style={{ cursor: 'pointer' }} onClick={(e) => handleViewTerms('pg_service_agreement', e)}>{t('view')}</div>
                      </span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="order_right">
            <div className="total_detail">
              <div className="total_count">
                <ul className="flex-2 column">
                  <li className="flex-btw"> 
                    {t('rental_return_branch')}
                    <p>
                      {rentalState.selectItem}
                      {returnState.selectItem && (
                        <>
                          <span className="text_small">/</span>
                          {returnState.selectItem}
                        </>
                      )}
                    </p>
                  </li>
                  <li className="flex-btw"> 
                    {t('total_product_quantity_txt')}
                    <p>{selectedSummary.totalEA}<span className="text_small">{t('quantity_count_txt')}</span></p>
                  </li>
                  <li className="flex-btw g-70">
                    &#8735;{t('rental_product4')}
                    <p>{selectedSummary.totalRentalEA}<span className="cap2_1">{t('quantity_count_txt')}</span></p>
                  </li>                    
                  <li className="flex-btw g-70">
                    &#8735;{t('additional_items_accessories_0617')}
                    <p>{selectedSummary.totalAccEA}<span className="cap2_1">{t('quantity_count_txt')}</span></p>
                  </li>
                </ul>         
              </div>
              <div className="total_day">
                <div className="flex-btw">
                  {t('total_rental_days_txt')}
                  <p className="text_small">
                    ({rentalDateTime.date.format('YYYY.MM.DD')}
                    {returnDateTime.date && (
                      <>~{returnDateTime.date.format('YYYY.MM.DD')}</>
                    )}
                    )
                    <span className="strong_b1">
                      {orderSummary?.totalInfo?.rental_period}
                    </span>{t('total_rental_days_count_0417')}
                  </p>
                </div>
              </div>
            </div>
            <div className="total_amount"> 
              <div className="flex-btw price_box">
                <p className="label red">{t('expected_payment_amount')}:</p>
                <p className="o_total_price red">
                  ₩<span>{formatLocalString(selectedSummary.totalPrice)}</span>
                </p> 
              </div> 
              <p 
                className="text_small_b g-70" 
                style={{textAlign:'right'}}
              >
                ₩{formatLocalString(selectedSummary.totalPrice)}{t('total_payment_amount_0617_2')}
              </p>
            </div>

            <div className="payment_btn">
              <button
                type="button"
                className="estimate_btn"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                className="pay_yellow_btn"
                onClick={handleSubmit}
              >
                {t('apply_for_rental')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <EquipmentRatingPopup 
        isPopup={isEquipmentPopup} 
        setIsPopup={setEquipmentPopup} 
      />

      <EstimatePopup 
        isPopup={isPopup} 
        setIsPopup={setIsPopup} 
        onConfirm={window.confirmSubmitEstimate}
      />

      <PopupLoginError
        isPopup={isPopupLoginError}
        setIsPopup={setIsPopupLoginError}
      />

      {/* 약관 팝업 */}
      <TermsPopup 
        isPopup={isTermsPopup}
        setIsPopup={setIsTermsPopup}
        title={popupContent.title}
        content={popupContent.content}
      />
    </div>
  )
};

export default QuotationForm;
